import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Receipt,
  X,
  FileText,
  Calculator,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const transactionTypes = [
  { id: 'income', label: 'Income', icon: TrendingUp, color: 'text-green-600' },
  { id: 'expense', label: 'Expense', icon: TrendingDown, color: 'text-red-600' },
  { id: 'invoice', label: 'Invoice (AR)', icon: FileText, color: 'text-blue-600' },
  { id: 'bill', label: 'Bill (AP)', icon: Receipt, color: 'text-orange-600' },
  { id: 'payroll', label: 'Payroll', icon: CreditCard, color: 'text-purple-600' }
];

const categories = [
  'Materials', 'Labor', 'Subcontractor', 'Equipment', 'Permits', 'Insurance',
  'Utilities', 'Marketing', 'Office', 'Vehicle', 'Miscellaneous'
];

export const Accounting = () => {
  const { api, isCOO } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    if (!isCOO) return;
    try {
      const [txnRes, summaryRes, jobsRes] = await Promise.all([
        api.get('/accounting/transactions'),
        api.get('/accounting/summary'),
        api.get('/jobs')
      ]);
      setTransactions(txnRes.data);
      setSummary(summaryRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Error fetching accounting data:', error);
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isCOO]);

  const handleAddTransaction = async (txnData) => {
    try {
      await api.post('/accounting/transactions', txnData);
      toast.success('Transaction added');
      fetchData();
      setShowAddModal(false);
    } catch (error) {
      toast.error('Failed to add transaction');
    }
  };

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch = txn.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          txn.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || txn.type === filterType;
    return matchesSearch && matchesType;
  });

  // Redirect if not COO
  if (!isCOO) {
    return <Navigate to="/portal" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Accounting</h1>
          <p className="text-gray-500 mt-1">Financial management & reporting</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          data-testid="add-transaction-btn"
        >
          <Plus className="w-4 h-4" />
          Add Transaction
        </button>
      </div>

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Total Income</span>
          </div>
          <p className="font-heading text-2xl text-green-600">${(summary?.income_statement?.total_income || 0).toLocaleString()}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-100 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Total Expenses</span>
          </div>
          <p className="font-heading text-2xl text-red-600">${(summary?.income_statement?.total_expenses || 0).toLocaleString()}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Net Income</span>
          </div>
          <p className={`font-heading text-2xl ${(summary?.income_statement?.net_income || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ${(summary?.income_statement?.net_income || 0).toLocaleString()}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#C6A87C]/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#C6A87C]" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Cash Position</span>
          </div>
          <p className="font-heading text-2xl text-[#C6A87C]">${(summary?.cash_position || 0).toLocaleString()}</p>
        </motion.div>
      </div>

      {/* AR/AP Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-700">Accounts Receivable</span>
            </div>
            <Clock className="w-5 h-5 text-blue-400" />
          </div>
          <p className="font-heading text-3xl text-blue-700">${(summary?.accounts_receivable || 0).toLocaleString()}</p>
          <p className="text-sm text-blue-600 mt-2">Outstanding invoices</p>
        </div>

        <div className="bg-orange-50 border border-orange-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ArrowDownRight className="w-5 h-5 text-orange-600" />
              <span className="font-semibold text-orange-700">Accounts Payable</span>
            </div>
            <Receipt className="w-5 h-5 text-orange-400" />
          </div>
          <p className="font-heading text-3xl text-orange-700">${(summary?.accounts_payable || 0).toLocaleString()}</p>
          <p className="text-sm text-orange-600 mt-2">Bills to pay</p>
        </div>

        <div className="bg-red-50 border border-red-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-700">Taxes Due</span>
            </div>
          </div>
          <p className="font-heading text-3xl text-red-700">${(summary?.taxes?.due || 0).toLocaleString()}</p>
          <p className="text-sm text-red-600 mt-2">Unpaid tax liabilities</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-[#C6A87C] text-[#C6A87C]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'jobs'
                ? 'border-[#C6A87C] text-[#C6A87C]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            By Job
          </button>
        </nav>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white min-w-[150px]"
            >
              <option value="">All Types</option>
              {transactionTypes.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Transactions Table */}
          <div className="bg-white border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase text-gray-500">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase text-gray-500">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase text-gray-500">Description</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase text-gray-500">Category</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase text-gray-500">Amount</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold uppercase text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map(txn => {
                      const typeConfig = transactionTypes.find(t => t.id === txn.type) || transactionTypes[0];
                      const TypeIcon = typeConfig.icon;
                      return (
                        <tr key={txn.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm">{new Date(txn.date).toLocaleDateString()}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
                              <span className="text-sm">{typeConfig.label}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">{txn.description}</td>
                          <td className="py-3 px-4 text-sm text-gray-500">{txn.category}</td>
                          <td className={`py-3 px-4 text-sm text-right font-medium ${
                            txn.type === 'income' ? 'text-green-600' : 
                            txn.type === 'expense' || txn.type === 'bill' || txn.type === 'payroll' ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {txn.type === 'expense' || txn.type === 'bill' || txn.type === 'payroll' ? '-' : '+'}${txn.amount.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {txn.paid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3" /> Paid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-700">
                                <Clock className="w-3 h-3" /> Pending
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-12 text-center text-gray-400">
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'jobs' && (
        <div className="bg-white border border-gray-100">
          <div className="divide-y divide-gray-100">
            {jobs.filter(j => j.status === 'active' || j.status === 'completed').map(job => {
              const jobTxns = transactions.filter(t => t.job_id === job.id);
              const jobIncome = jobTxns.filter(t => t.type === 'income' && t.paid).reduce((sum, t) => sum + t.amount, 0);
              const jobExpenses = jobTxns.filter(t => t.type === 'expense' && t.paid).reduce((sum, t) => sum + t.amount, 0);
              const profitMargin = job.estimated_value > 0 ? ((job.estimated_value - jobExpenses) / job.estimated_value * 100) : 0;
              
              return (
                <div key={job.id} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-medium">{job.client_name}</h3>
                      <p className="text-sm text-gray-500">{job.project_type}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Contract Value</p>
                      <p className="font-medium">${job.estimated_value.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Income</p>
                      <p className="font-medium text-green-600">${jobIncome.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Expenses</p>
                      <p className="font-medium text-red-600">${jobExpenses.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Margin</p>
                      <p className={`font-medium ${profitMargin >= 20 ? 'text-green-600' : profitMargin >= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {profitMargin.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      <AnimatePresence>
        {showAddModal && (
          <TransactionModal
            jobs={jobs}
            onClose={() => setShowAddModal(false)}
            onSave={handleAddTransaction}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Transaction Modal
const TransactionModal = ({ jobs, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    type: 'expense',
    category: 'Materials',
    description: '',
    amount: '',
    job_id: '',
    vendor: '',
    due_date: '',
    paid: false
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="font-heading text-xl">Add Transaction</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...formData, amount: parseFloat(formData.amount) }); }} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
            >
              {transactionTypes.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Amount *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  data-testid="txn-amount-input"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Description *</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="Brief description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Job (Optional)</label>
              <select
                value={formData.job_id}
                onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                <option value="">No job linked</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.client_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Vendor</label>
              <input
                type="text"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="Home Depot"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <input
              type="checkbox"
              id="paid"
              checked={formData.paid}
              onChange={(e) => setFormData({ ...formData, paid: e.target.checked })}
              className="w-4 h-4 accent-[#C6A87C]"
            />
            <label htmlFor="paid" className="text-sm text-gray-600">Mark as paid</label>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]"
              data-testid="save-txn-btn"
            >
              Add Transaction
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
